import type {
  IAssistantPermissionsResult,
  IResolveAssistantPermissionsFromChecksProps,
  IResolveAssistantPermissionsProps,
} from "../types/assistant-permissions";
import { EAssistantSharePermission } from "../types/assistant-share";
import { EProjectPermission } from "../types/permission";

// NOTE: The permissions both clients ask `usePermissionCheck` for, and the
// order the boolean tuple comes back in. It lives here, next to the resolver
// that reads that tuple, so the list and its meaning cannot drift — and so the
// two platform hooks stay thin wrappers rather than two identical copies of
// this array plus its five-way destructuring (Sonar had the pair at 80%
// duplicated for exactly that reason).
export const ASSISTANT_PERMISSION_CHECKS: EProjectPermission[] = [
  EProjectPermission.PRIVATE_ASSISTANT_WRITE,
  EProjectPermission.PRIVATE_ASSISTANT_DELETE,
  EProjectPermission.PRIVATE_ASSISTANT_SHARE_WRITE,
  EProjectPermission.PUBLIC_ASSISTANT_WRITE,
  EProjectPermission.PUBLIC_ASSISTANT_DELETE,
];

// NOTE: Mirrors the server's per-type strict gating model:
//   - Owner of a private assistant needs PRIVATE_ASSISTANT_WRITE/DELETE.
//   - Owner of a public assistant needs PUBLIC_ASSISTANT_WRITE/DELETE (the
//     per-type rule applies to owners too — server-side check is in
//     resolveAndCheckIsPublic + deleteAssistant).
//   - Non-owner of a private assistant needs an EDITOR share AND
//     PRIVATE_ASSISTANT_WRITE on their role to edit. VIEWER share never edits.
//     Non-owner of private cannot delete regardless of role permission.
//   - Non-owner of a public assistant needs PUBLIC_ASSISTANT_WRITE to edit
//     (or an EDITOR share — share-based fallback still works for public).
//     Non-owner of public needs PUBLIC_ASSISTANT_DELETE to delete.
// Share button is owner-only and gated on PRIVATE_ASSISTANT_SHARE_WRITE.
// Leave button is for the share recipient only.
// Visibility toggle is owner-only and needs BOTH write permissions — see below.
export const resolveAssistantPermissions = ({
  assistant,
  isPersonalProject,
  hasPrivateAssistantWritePermission,
  hasPrivateAssistantDeletePermission,
  hasPrivateAssistantShareWritePermission,
  hasPublicAssistantWritePermission,
  hasPublicAssistantDeletePermission,
}: IResolveAssistantPermissionsProps): IAssistantPermissionsResult => {
  // NOTE: App / system / recommended assistants are managed via a separate
  // admin flow — no action in this menu applies to them, and neither does
  // anything below when there is no assistant to reason about.
  if (!assistant || assistant.isAppAssistant) {
    return {
      isHiddenEdit: true,
      isHiddenDelete: true,
      isHiddenShare: true,
      isHiddenLeave: true,
      isHiddenToggleVisibility: true,
    };
  }

  const sharePermission = assistant.sharedBy?.permission ?? null;
  const isViewerShare = sharePermission === EAssistantSharePermission.VIEWER;
  const isEditorShare = sharePermission === EAssistantSharePermission.EDITOR;
  const isOwner = assistant.isOwnedByYou === true;
  const isSharedRecipient = assistant.sharedBy != null;

  const resolveIsHiddenEdit = (): boolean => {
    if (isViewerShare) return true;

    if (assistant.isPublic) {
      // Public assistant: anyone with PUBLIC_WRITE can edit, OR an EDITOR-share
      // recipient can edit (server's checkNonOwnerUpdateAccess preserves this).
      // Owners are NOT exempt — they also need PUBLIC_WRITE.
      if (hasPublicAssistantWritePermission) return false;
      if (isEditorShare) return false;
      return true;
    }

    // Private assistant.
    if (isOwner) {
      return !hasPrivateAssistantWritePermission;
    }

    // Non-owner of a private assistant: EDITOR share AND PRIVATE_ASSISTANT_WRITE
    // are both required. This mirrors checkNonOwnerUpdateAccess on the server.
    if (!isEditorShare) return true;
    return !hasPrivateAssistantWritePermission;
  };

  const resolveIsHiddenDelete = (): boolean => {
    if (assistant.isPublic) {
      // Public delete is gated solely by PUBLIC_DELETE — owners aren't exempt.
      // Share recipients still can't delete (sharing on a public assistant
      // isn't possible anyway, but defensively hide).
      if (isSharedRecipient) return true;
      return !hasPublicAssistantDeletePermission;
    }

    // Private assistant: only the owner with PRIVATE_DELETE can delete.
    if (!isOwner) return true;
    return !hasPrivateAssistantDeletePermission;
  };

  const resolveIsHiddenShare = (): boolean => {
    // Sharing is a private-assistant concept (server rejects sharing a public one)
    // and only the owner can initiate it. NSAI-4775: a public assistant offers the
    // visibility toggle instead, so the owner can see why Share is absent and undo
    // it — rather than the action simply vanishing with no explanation.
    if (assistant.isPublic) return true;
    if (!isOwner) return true;
    return !hasPrivateAssistantShareWritePermission;
  };

  const resolveIsHiddenToggleVisibility = (): boolean => {
    // NOTE: Owner-only, and both write permissions are required in EITHER
    // direction: the server gates the assistant's CURRENT type via
    // checkWritePermissionForAssistantType and the TARGET type via
    // resolveAndCheckIsPublic, so a user holding only one of the two would meet
    // a 403 on the flip. Same rule the visibility <select> uses in the form.
    if (!isOwner) return true;
    if (isPersonalProject) return true;
    return (
      !hasPrivateAssistantWritePermission || !hasPublicAssistantWritePermission
    );
  };

  return {
    isHiddenEdit: resolveIsHiddenEdit(),
    isHiddenDelete: resolveIsHiddenDelete(),
    isHiddenShare: resolveIsHiddenShare(),
    // Only an active share recipient sees the Leave action.
    isHiddenLeave: !isSharedRecipient,
    isHiddenToggleVisibility: resolveIsHiddenToggleVisibility(),
  };
};

// NOTE: The platform-facing entry point: takes the tuple straight from
// `usePermissionCheck(ASSISTANT_PERMISSION_CHECKS)` and names its entries once.
// A short tuple (permissions still loading) reads as denied rather than
// granting an action the server would refuse.
export const resolveAssistantPermissionsFromChecks = ({
  assistant,
  isPersonalProject,
  permissionChecks,
}: IResolveAssistantPermissionsFromChecksProps): IAssistantPermissionsResult => {
  const [
    hasPrivateAssistantWritePermission,
    hasPrivateAssistantDeletePermission,
    hasPrivateAssistantShareWritePermission,
    hasPublicAssistantWritePermission,
    hasPublicAssistantDeletePermission,
  ] = permissionChecks;

  return resolveAssistantPermissions({
    assistant,
    isPersonalProject,
    hasPrivateAssistantWritePermission:
      hasPrivateAssistantWritePermission === true,
    hasPrivateAssistantDeletePermission:
      hasPrivateAssistantDeletePermission === true,
    hasPrivateAssistantShareWritePermission:
      hasPrivateAssistantShareWritePermission === true,
    hasPublicAssistantWritePermission:
      hasPublicAssistantWritePermission === true,
    hasPublicAssistantDeletePermission:
      hasPublicAssistantDeletePermission === true,
  });
};
